import { useCallback } from 'react';

import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';
import to from 'await-to-js';

import { notify } from '@/components/toast';
import { UPDATE_ARCHIVE_CONDITION_MUTATION } from '@/gql/queries/workflow-config.gql';
import { TYPE_WORKFLOW_CONFIG } from '@/gql/types';

export function useUpdateArchiveCondition(
  workflowConfigRowId: string | undefined
) {
  const [updateArchiveConditionMutation] = useMutation(
    UPDATE_ARCHIVE_CONDITION_MUTATION
  );

  const updateArchiveCondition = useCallback(
    async (archiveCondition: unknown) => {
      if (!workflowConfigRowId) return;

      const promise = updateArchiveConditionMutation({
        variables: {
          workflowConfigRowId,
          archiveCondition
        },
        optimisticResponse: {
          updateWorkflowConfigByRowId: {
            workflowConfig: {
              rowId: workflowConfigRowId,
              archiveCondition,
              __typename: TYPE_WORKFLOW_CONFIG
            },
            __typename: 'UpdateWorkflowConfigPayload'
          }
        }
      });

      notify.promise(promise, {
        loading: 'Updating the archive condition...',
        success: 'The archive condition was updated',
        error: error => {
          Sentry.captureException(error, {
            tags: { component: 'useUpdateArchiveCondition' },
            extra: { workflowConfigRowId }
          });
          return 'Something went wrong during the archive condition update...';
        }
      });

      await to(promise);
    },
    [workflowConfigRowId, updateArchiveConditionMutation]
  );

  return updateArchiveCondition;
}
